/**
 * The five ways a semantic accent is allowed to be painted.
 *
 * Badge, Chip, Alert and StatusPanel all used to carry their own copy of
 * `bg-streak-soft text-streak`, which is how the streak badge ended up at
 * 2.68:1 in light while the same colour pair passed everywhere else — nobody
 * could see that the six of them were the same decision written six times.
 *
 * Each map is one *complete* recipe, never a fragment: `TONE_SOFT` already
 * carries the text colour that is AA on its own fill, `TONE_SOLID` already
 * carries the label that is AA on *that* fill. Callers pick a recipe; they
 * never assemble one.
 *
 * The class names are written out in full and never interpolated —
 * `bg-${tone}-soft` produces nothing, because Tailwind reads source text.
 *
 * Material primary is one teal family in both themes. `focus` remains a legacy
 * semantic name, but it resolves to the same accent recipes as `accent` so a
 * primitive cannot reintroduce a second teal. Neutral fills stay alpha-based
 * so they composite correctly on every Material surface.
 */

/**
 * A semantic accent.
 *
 * `accent` and `focus` resolve to the same Material primary. Lane colours are
 * a separate data scale and are never a tone.
 */
export type AccentTone = "accent" | "focus" | "pomodoro" | "streak" | "plan" | "danger";

/** An accent, or the quiet default that carries no meaning at all. */
export type Tone = "neutral" | AccentTone;

/**
 * The tinted fill with AA text on it — the badge and chip recipe.
 * Every pair here clears 4.5:1 in both themes.
 */
export const TONE_SOFT: Record<Tone, string> = {
  neutral: "bg-fill-2 text-muted",
  accent: "bg-accent-soft text-accent-ink",
  focus: "bg-focus-soft text-focus",
  pomodoro: "bg-pomodoro-soft text-pomodoro",
  streak: "bg-streak-soft text-streak",
  plan: "bg-plan-soft text-plan",
  danger: "bg-danger-soft text-danger",
};

/**
 * A 3:1 outline for a soft fill that needs a visible boundary — a badge on a
 * busy row, a chip on a tinted lane. The `-soft` fills are deliberately calm
 * against the page (~1.2:1); this is how a single one of them gets an edge
 * without turning every badge into a saturated block.
 *
 * Drawn at `--busee-hairline` (0.5px above 1dppx). `border-hairline` sets the
 * width *and* the style, so the colour utility is all that follows it.
 */
export const TONE_EDGE: Record<Tone, string> = {
  neutral: "border-hairline border-separator",
  accent: "border-hairline border-accent-edge",
  focus: "border-hairline border-focus-edge",
  pomodoro: "border-hairline border-pomodoro-edge",
  streak: "border-hairline border-streak-edge",
  plan: "border-hairline border-plan-edge",
  danger: "border-hairline border-danger-edge",
};

/**
 * A solid fill with the label that is AA on it. This is the *only* correct
 * treatment for a selected compact control (`index.css` rule 4) and for the one
 * priority that has to shout.
 *
 * `accent` and legacy `focus` use the same theme-adaptive primary/on-primary
 * pair, so selected and prominent controls cannot drift.
 */
export const TONE_SOLID: Record<Tone, string> = {
  neutral: "bg-line-strong text-inverted",
  accent: "bg-accent-fill text-accent-fill-on",
  focus: "bg-focus text-focus-on",
  pomodoro: "bg-pomodoro text-pomodoro-on",
  streak: "bg-streak text-streak-on",
  plan: "bg-plan text-plan-on",
  danger: "bg-danger text-danger-on",
};

/**
 * The bare accent as text or as an icon on the page surface.
 *
 * `accent` uses the role-specific ink alias. The alias now resolves to the same
 * AA Material teal as every other primary role.
 */
export const TONE_TEXT: Record<Tone, string> = {
  neutral: "text-muted",
  accent: "text-accent-ink",
  focus: "text-focus",
  pomodoro: "text-pomodoro",
  streak: "text-streak",
  plan: "text-plan",
  danger: "text-danger",
};

/**
 * The bare accent as a fill for a marker, rail or progress bar.
 *
 * `focus` and `accent` are the same solid Material primary on purpose. Progress
 * and status marks therefore use the same teal as selected controls.
 */
export const TONE_FILL: Record<Tone, string> = {
  neutral: "bg-line-strong",
  accent: "bg-accent",
  focus: "bg-accent",
  pomodoro: "bg-pomodoro",
  streak: "bg-streak",
  plan: "bg-plan",
  danger: "bg-danger",
};

/**
 * Attachment-only transient states. They live here so the exact measured
 * foreground/fill/edge combinations are not rebuilt inside the attachment
 * component, while preserving the existing drag and failure composites.
 */
export const ATTACHMENT_DROP_TONE =
  "border-2 border-dashed border-accent bg-accent-soft/90 text-accent-ink";
export const ATTACHMENT_BROKEN_TONE =
  "border-dashed border-danger bg-danger-soft text-danger";
export const ATTACHMENT_FAILURE_EDGE = "border border-dashed border-danger";
export const ATTACHMENT_FAILURE_OVERLAY = "bg-danger-soft/85 text-danger";

/**
 * Reveal-on-hover, done so that it still exists on a phone.
 *
 * The old rule was a bare `opacity-0` plus `group-hover:opacity-100`, and since
 * touch never hovers, every row action on the tasks screen was permanently
 * invisible and unreachable in the PWA (P7). Here the *resting* state is
 * visible, and only a device that can actually hover opts into hiding it.
 *
 * The two rules cannot fight: `group-hover/row:…` compiles to
 * `.group\/row:hover .x`, which outranks the plain `.x` inside the media query
 * on specificity, so the reveal wins regardless of how the sheet is ordered.
 *
 * `and (not (any-pointer: coarse))` rather than `(hover: hover)` alone, because
 * `hover` and `pointer` describe only the device's *primary* pointer. A
 * touchscreen laptop and a Surface report `hover: hover` with a `pointer: fine`
 * trackpad, so the bare query hid every row action from the finger that was
 * about to press it — the same P7 failure, on a narrower class of device.
 * `any-pointer` asks whether a coarse pointer exists at all, which is the
 * question being asked. Verified in the built stylesheet: it compiles to
 * `@media (hover:hover) and (not (any-pointer:coarse))` and still lands after
 * the resting `.opacity-100`.
 *
 * Requires `group/row` on the row container.
 */
export const hoverReveal =
  "opacity-100 [@media(hover:hover)_and_(not_(any-pointer:coarse))]:opacity-0 " +
  "group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-within:opacity-100";

/**
 * The 44px tap floor for a control that genuinely cannot grow.
 *
 * Most controls simply take `pointer-coarse:size-tap` and get bigger. A few
 * cannot: a ✕ pinned to the corner of a 96px thumbnail would swallow the
 * thumbnail, and a `Retry` link inside a failed tile has nowhere to expand
 * into. Those get an invisible halo instead — the drawn size is unchanged and
 * the *hit area* grows by 12px on every side, which is 20 + 24 = 44.
 *
 * `-inset-3` is measured against a 20px control. Requires the element to
 * establish a positioning context (`relative`, or an `absolute` of its own —
 * do **not** add `relative` to something already positioned, it would move it).
 */
export const tapHalo =
  "pointer-coarse:before:absolute pointer-coarse:before:-inset-3 pointer-coarse:before:content-['']";

/**
 * The one press feedback in the app: iOS dims a control while your finger is on
 * it. It is opacity rather than a colour swap so it reads identically on a
 * filled button, a tinted one and a plain one, and so it can never be mistaken
 * for a *selection* (which is a saturation change and is instant).
 *
 * `dur-fast` because a press must feel welded to the finger; anything longer
 * lags behind a fast tap and lands after the finger has left.
 */
export const pressDim = "transition-opacity dur-fast active:opacity-70";
